import '@cognitoforms/workflow-diagram';
import type { ComponentOptions } from 'vue';
import type { WorkflowObject, FormAction } from 'src/views/admin/utils/workflow-diagram.types';
import { ScrollBehavior } from 'src/views/admin/utils/workflow-diagram.types';
import type { WorkflowActionPrefillConfiguration } from '@cognitoforms/types/server-types/forms/model/workflow-action-prefill-configuration.ts';
import type { AutoCreateEntriesOption } from '@cognitoforms/types/server-types/forms/model/auto-create-entries-option.ts';
import type { FieldInfo } from '@cognitoforms/types/server-types/model/field-info';
import type { PrefillConfigValidationResponse } from '@cognitoforms/types/server-types/forms/model/prefill-config-validation-response';
import type { FieldToken } from 'src/views/admin/utils/build';
import EmailCardList from 'src/views/build/EmailCardList.vue';
import CreateEntriesCardList, { type TargetFormOption } from 'src/views/build/CreateEntriesCardList.vue';
import { ensureCognitoGlobal } from './utils/globals-management';
import { getWorkflowRoleIconForAssignment } from 'src/utilities/workflow-role-icon';
import getScrollBarWidth from '@cognitoforms/element-ui/src/utils/scrollbar-width';

// Type for the workflow diagram custom element
interface WorkflowDiagramElement extends HTMLElement {
	visible: boolean;
	workflow: WorkflowObject;
	isDarkMode: boolean;
	setContainerDimensions: boolean;
	currentAction: string | null;
	enableTooltips: boolean;
}

// Dropdown component prop types
type EmailCardListProps = {
	emails: Array<{
		Id: string | number;
		index: number;
		subjectHtml: string;
		toHtml: string;
		sendWhenPreview: string;
		recipient: string | null;
		roleIcon: string | null;
	}>;
	showWhen: boolean;
	showHeader: boolean;
};

type CreateEntriesCardListProps = {
	formId: number;
	actionId: number;
	formName: string;
	actionName: string;
	prefillConfigurations: WorkflowActionPrefillConfiguration[];
	currentFormFieldInfos: FieldToken[];
	showCreateEntries: boolean;
	showHeader: boolean;
	getAutoCreateEntriesOptions: (currentFormLookupFieldInfos: FieldInfo[], targetFormId: string) => Promise<AutoCreateEntriesOption>;
	getTargetFormOptions: (currentFormReferences: string[]) => Promise<TargetFormOption[]>;
	validatePrefillConfigs: (targetFormIds: string[], currentFormLookupFieldInfos: FieldInfo[]) => Promise<PrefillConfigValidationResponse>;
};

// Dropdown positioning constants
const VIEWPORT_PADDING = 8; // Padding from viewport edges
const DROPDOWN_GAP = 4; // Gap between target element and dropdown
const DROPDOWN_MIN_WIDTH = 300;
const DROPDOWN_MAX_WIDTH = 327;
const DROPDOWN_ESTIMATED_HEIGHT = 300;

let attachedDiagram: HTMLElement | null = null;
let activeDropdownInstance: Vue | null = null;
let activeDropdownType: string | null = null; // Tracks which action's dropdown is open
let activeDropdownTarget: HTMLElement | null = null;
let clickOutsideHandler: ((event: MouseEvent) => void) | null = null;
let scrollHandler: ((event: Event) => void) | null = null;
let resizeHandler: (() => void) | null = null;

function getActionByName(actionName: string): FormAction | undefined {
	const Cognito = ensureCognitoGlobal();
	const actions: FormAction[] = Cognito.Forms.model.currentForm.get_Actions();
	return actions.find((a) => a.get_ActionName() === actionName && !a.get_IsArchived());
}

/**
 * Selects an action only if it's not already selected.
 * @returns true if the action was already selected, false if it needed to be selected
 */
function ensureActionSelected(action: FormAction, scrollBehavior: ScrollBehavior = ScrollBehavior.PaneTop): boolean {
	const Cognito = ensureCognitoGlobal();
	const currentAction = Cognito.Forms.model.currentForm.get_currentAction();
	const isAlreadySelected = !!currentAction && currentAction.get_Id() === action.get_Id();

	if (!isAlreadySelected) {
		Cognito.Forms.selectAction({ id: action.get_Id() }, scrollBehavior);
	}

	return isAlreadySelected;
}

function closeDropdown() {
	if (clickOutsideHandler) {
		document.removeEventListener('click', clickOutsideHandler, true);
		clickOutsideHandler = null;
	}
	if (scrollHandler) {
		window.removeEventListener('scroll', scrollHandler, true);
		scrollHandler = null;
	}
	if (resizeHandler) {
		window.removeEventListener('resize', resizeHandler);
		resizeHandler = null;
	}
	if (activeDropdownInstance && activeDropdownInstance.$el) {
		(activeDropdownInstance.$el as HTMLElement).style.display = 'none';
	}
	activeDropdownType = null;
	activeDropdownTarget?.removeAttribute('data-dropdown-active');
	activeDropdownTarget = null;
}

function applyDropdownStyles(el: HTMLElement, rect: DOMRect) {
	const viewportHeight = window.innerHeight;
	const viewportWidth = window.innerWidth;
	const spaceBelow = viewportHeight - rect.bottom - VIEWPORT_PADDING;
	const spaceAbove = rect.top - VIEWPORT_PADDING;

	// Check if there's enough space below, otherwise open upward
	const openUpward = spaceBelow < DROPDOWN_ESTIMATED_HEIGHT && spaceAbove > spaceBelow;

	const maxHeight = openUpward
		? Math.min(spaceAbove, viewportHeight * 0.5 - 50)
		: Math.min(spaceBelow, viewportHeight * 0.5 - 50);

	el.style.position = 'fixed';
	el.style.zIndex = '10';
	el.style.minWidth = `${DROPDOWN_MIN_WIDTH}px`;
	el.style.maxWidth = `${DROPDOWN_MAX_WIDTH}px`;
	el.style.maxHeight = `${maxHeight}px`;
	el.style.border = '1px solid var(--border-subtle)';
	el.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
	el.style.background = 'var(--bg-primary)';
	el.style.overflowY = 'auto';
	el.style.overflowX = 'hidden';

	// Vertical positioning
	if (openUpward) {
		el.style.bottom = `${viewportHeight - rect.top + DROPDOWN_GAP}px`;
		el.style.top = 'auto';
	}
	else {
		el.style.top = `${rect.bottom + DROPDOWN_GAP}px`;
		el.style.bottom = 'auto';
	}

	// Horizontal positioning
	const idealLeft = rect.left;
	const scrollbarWidth = getScrollBarWidth();
	const maxLeft = viewportWidth - DROPDOWN_MAX_WIDTH - scrollbarWidth - VIEWPORT_PADDING;
	const minLeft = VIEWPORT_PADDING;

	// Clamp position between min and max
	const leftPosition = Math.min(Math.max(idealLeft, minLeft), maxLeft);

	el.style.left = `${leftPosition}px`;

	// Add scrollbar class for consistent scrollbar styling
	el.classList.add('scrollbar');
}

function setupDropdown(
	target: HTMLElement,
	ComponentClass: ComponentOptions<Vue>,
	propsData: EmailCardListProps | CreateEntriesCardListProps,
	setupEvents: (instance: Vue) => void,
	dropdownType: string
) {
	const rect = target.getBoundingClientRect();

	// Destroy and recreate to get fresh props/data
	if (activeDropdownInstance) {
		if (activeDropdownInstance.$el && activeDropdownInstance.$el.parentNode) {
			activeDropdownInstance.$el.parentNode.removeChild(activeDropdownInstance.$el);
		}
		activeDropdownInstance.$destroy();
		activeDropdownInstance = null;
	}

	// Create new instance with fresh data
	const DropdownComponent = Vue.extend(ComponentClass);
	activeDropdownInstance = new DropdownComponent({ propsData });
	activeDropdownType = dropdownType;
	activeDropdownTarget = target;
	target.setAttribute('data-dropdown-active', '');

	setupEvents(activeDropdownInstance);

	activeDropdownInstance.$mount();
	const el = activeDropdownInstance.$el as HTMLElement;
	el.classList.add('workflow-dropdown-context');
	applyDropdownStyles(el, rect);
	document.body.appendChild(el);

	clickOutsideHandler = (event: MouseEvent) => {
		const dropdown = activeDropdownInstance?.$el;
		const clickedElement = event.target as Node;
		const path = event.composedPath();
		// Don't close if clicking on the dropdown, trigger, or any dialog
		if (dropdown &&
			!dropdown.contains(clickedElement) &&
			!path.includes(target) &&
			!(clickedElement as HTMLElement).closest('.el-dialog, .c-dialog, .dialog')) {
			closeDropdown();
		}
	};

	scrollHandler = (event: Event) => {
		const dropdown = activeDropdownInstance?.$el;
		const scrollElement = event.target as HTMLElement;

		// Don't close if scrolling within the dropdown itself
		if (dropdown && dropdown.contains(scrollElement)) {
			return;
		}

		// Don't close if scrolling the workflow pane
		if (scrollElement.id === 'c-forms-settings' || scrollElement.closest('#c-forms-settings')) {
			return;
		}

		// Close if scrolling the builder canvas or other containers
		closeDropdown();
	};

	resizeHandler = () => {
		closeDropdown();
	};

	// Delay to prevent immediate closing from the click that opened it
	setTimeout(() => {
		if (clickOutsideHandler) {
			document.addEventListener('click', clickOutsideHandler, true);
		}
		if (scrollHandler) {
			window.addEventListener('scroll', scrollHandler, true);
		}
		if (resizeHandler) {
			window.addEventListener('resize', resizeHandler);
		}
	}, 100);
}

function showEmailDropdown(action: FormAction, actionName: string, target: HTMLElement) {
	const Cognito = ensureCognitoGlobal();
	const roles = Cognito.Forms.model.currentForm.get_Roles();

	const emails = action.get_Emails ? action.get_Emails().map((email, index) => ({
		Id: email.get_Id ? email.get_Id() : index,
		index: index,
		subjectHtml: email.get_subjectHtml ? email.get_subjectHtml() : '',
		toHtml: email.get_toHtml ? email.get_toHtml() : '',
		sendWhenPreview: email.get_sendWhenPreview ? email.get_sendWhenPreview() : '',
		recipient: email.get_recipient ? email.get_recipient() : null,
		hasErrors: email.get_hasErrors ? email.get_hasErrors() : false,
		roleIcon: getWorkflowRoleIconForAssignment(
			email.get_RoleAssignment ? email.get_RoleAssignment() : null,
			roles
		)
	})) : [];

	setupDropdown(
		target,
		EmailCardList,
		{ emails, showWhen: true, showHeader: true },
		(instance) => {
			instance.$on('add-email', () => {
				closeDropdown();
				if (Cognito.Forms.handleActionAddEmail) {
					Cognito.Forms.handleActionAddEmail();
				}
			});

			instance.$on('edit-email', (emailIndex: number) => {
				closeDropdown();
				if (Cognito.Forms.handleActionEditEmail) {
					Cognito.Forms.handleActionEditEmail(emailIndex);
				}
			});

			instance.$on('email-deleted', (emailIndex: number) => {
				closeDropdown();
				Cognito.Forms.handleActionEmailDelete(emailIndex);
			});
		},
		`email-${actionName}`
	);
}

function showAutoCreateEntriesDropdown(action: FormAction, actionName: string, target: HTMLElement, retryCount = 0) {
	const Cognito = ensureCognitoGlobal();

	const prefillConfigs = action.get_prefillConfigList ? action.get_prefillConfigList() : [];
	const currentFormFieldInfos = Cognito.Forms.model.currentForm.get_currentFormFieldInfos ? Cognito.Forms.model.currentForm.get_currentFormFieldInfos() : [];

	setupDropdown(
		target,
		CreateEntriesCardList,
		{
			formId: Cognito.Forms.model.currentForm.get_Id(),
			actionId: action.get_Id(),
			formName: Cognito.Forms.model.currentForm.get_InternalName(),
			actionName: actionName,
			prefillConfigurations: prefillConfigs,
			currentFormFieldInfos: currentFormFieldInfos,
			showCreateEntries: true,
			showHeader: true,
			getAutoCreateEntriesOptions: Cognito.Forms.getAutoCreateEntriesOptions,
			getTargetFormOptions: Cognito.Forms.getTargetFormOptions,
			validatePrefillConfigs: Cognito.Forms.validatePrefillConfigs
		},
		(instance) => {
			instance.$on('opening-dialog', () => {
				closeDropdown();
			});

			instance.$on('save-prefill-configuration', (prefillConfig: object, prefillConfigIndex: number) => {
				Cognito.Forms.savePrefillConfiguration(prefillConfig, prefillConfigIndex);
				closeDropdown();
			});

			instance.$on('delete-prefill-configuration', (prefillConfigIndex: number) => {
				Cognito.Forms.deletePrefillConfiguration(prefillConfigIndex);
				closeDropdown();
			});
		},
		`createEntries-${actionName}`
	);
}

function handleNodeClick(type: 'action' | 'status', name: string) {
	if (type === 'action') {
		const action = getActionByName(name);

		if (action) {
			const wasAlreadySelected = ensureActionSelected(action);
			// If action was already selected, scroll to top
			if (wasAlreadySelected) {
				$('#c-forms-settings').animate({ scrollTop: 0 }, 300);
			}
		}
	}
	else if (type === 'status') {
		const Cognito = ensureCognitoGlobal();
		Cognito.Forms.openWorkflowPanel(ScrollBehavior.None);
		Cognito.Forms.collapseSettingsSections('statuses');

		// Find status by name and pass the status object
		const statuses = Cognito.Forms.model.currentForm.get_EntryStatuses();
		const status = statuses.find((s: any) => s.get_Name() === name);

		if (status)
			Cognito.Forms.openStatusDialog(status);
	}
}

function handleRoleClick(roleName: string | undefined) {
	const Cognito = ensureCognitoGlobal();
	const isOnWorkflowPane = $('#c-forms-settings-workflow').is(':visible');
	const currentAction = Cognito.Forms.model.currentForm.get_currentAction();
	const isOnWorkflowOverview = isOnWorkflowPane && !currentAction;

	const scrollBehavior = isOnWorkflowOverview ? ScrollBehavior.None : ScrollBehavior.PaneTop;

	if (roleName === undefined) {
		Cognito.Forms.openRoleSettings(scrollBehavior).then(() => {
			Cognito.Forms.scrollInWorkflowPanel('.c-forms-settings-workflow-roles-section');
		});
	}
	else {
		// Call the editRole function exposed by WorkflowRoles.vue
		if (Cognito.Forms.editRole) {
			Cognito.Forms.editRole(roleName);

			Cognito.Forms.openRoleSettings(scrollBehavior).then(() => {
				Cognito.Forms.scrollInWorkflowPanel('.workflow-roles', roleName);
			});
		}
	}
}

function handleEmailClick(actionName: string, target?: HTMLElement) {
	const Cognito = ensureCognitoGlobal();
	if (target) {
		const dropdownType = `email-${actionName}`;

		if (activeDropdownType === dropdownType) {
			closeDropdown();
			return;
		}

		const action = getActionByName(actionName);

		if (action) {
			ensureActionSelected(action);
			showEmailDropdown(action, actionName, target);
			Cognito.Forms.scrollInWorkflowPanel('.c-forms-settings-workflow-action-emails');
		}
	}
}

function handleCreateEntriesClick(actionName: string, target?: HTMLElement) {
	const Cognito = ensureCognitoGlobal();
	if (target) {
		const dropdownType = `createEntries-${actionName}`;

		if (activeDropdownType === dropdownType) {
			closeDropdown();
			return;
		}

		const action = getActionByName(actionName);

		if (action) {
			ensureActionSelected(action);
			showAutoCreateEntriesDropdown(action, actionName, target);
			Cognito.Forms.scrollInWorkflowPanel('.c-forms-settings-workflow-create-entries');
		}
	}
}

function handleClearFieldsClick(actionName: string) {
	const Cognito = ensureCognitoGlobal();
	const action = getActionByName(actionName);

	if (action) {
		ensureActionSelected(action);
		Cognito.Forms.scrollInWorkflowPanel('.c-forms-settings-workflow-action-clear-fields');
	}
}

function handleRedirectClick(actionName: string) {
	const Cognito = ensureCognitoGlobal();
	const action = getActionByName(actionName);

	if (action) {
		ensureActionSelected(action);
		Cognito.Forms.scrollInWorkflowPanel('.c-forms-settings-workflow-action-confirmation-behavior');
	}
}

// Set up event handlers once on the diagram element
function setupDiagramEventHandlers(diagram: WorkflowDiagramElement) {
	const handlers = {
		'node-click': (event: CustomEvent) => {
			const { type, name } = event.detail[0] || event.detail;
			handleNodeClick(type, name);
		},
		'email-click': (event: CustomEvent) => {
			const { actionName, target } = event.detail[0] || event.detail;
			handleEmailClick(actionName, target);
		},
		'create-entries-click': (event: CustomEvent) => {
			const { actionName, target } = event.detail[0] || event.detail;
			handleCreateEntriesClick(actionName, target);
		},
		'role-click': (event: CustomEvent) => {
			const roleName = Array.isArray(event.detail) ? event.detail[0] : event.detail;
			handleRoleClick(roleName);
		},
		'clear-fields-click': (event: CustomEvent) => {
			const detail = event.detail[0] || event.detail;
			const actionName = detail.actionName;
			handleClearFieldsClick(actionName);
		},
		'redirect-click': (event: CustomEvent) => {
			const detail = event.detail[0] || event.detail;
			const actionName = detail.actionName;
			handleRedirectClick(actionName);
		}
	};

	Object.entries(handlers).forEach(([eventName, handler]) => {
		diagram.addEventListener(eventName, handler as EventListener);
	});
}

export function initDiagram(wf: WorkflowObject) {
	let diagram = document.getElementById('workflow-diagram') as WorkflowDiagramElement | null;

	if (!diagram) {
		const container = document.getElementById('workflow-diagram-container');
		if (!container) {
			console.error('Workflow diagram container not found in DOM');
			return;
		}
		diagram = document.createElement('cog-workflow-diagram') as unknown as WorkflowDiagramElement;
		diagram.id = 'workflow-diagram';
		diagram.visible = true;
		container.appendChild(diagram);
	}

	if (attachedDiagram !== diagram) {
		attachedDiagram = diagram;
		setupDiagramEventHandlers(diagram);
	}

	diagram.workflow = wf;
	diagram.isDarkMode = document.documentElement.getAttribute('data-color-scheme') === 'dark';
	diagram.enableTooltips = true;
}
